/**
 * Pure helpers behind `ui/StateBlock` (DESIGN_SYSTEM.md §14, PRD §4.13).
 * No React, so the unit tests can run them under node.
 */

export type StateKind = 'loading' | 'empty' | 'error' | 'unavailable' | 'denied';

/** `forbidden` is accepted as an alias of `denied` (403). */
export type StateKindInput = StateKind | 'forbidden';

export function normalizeKind(kind: StateKindInput): StateKind {
  return kind === 'forbidden' ? 'denied' : kind;
}

/** HTTP status carried by an API rejection (axios error or the services/api.ts envelope). */
export function errorStatus(err: unknown): number | undefined {
  if (!err || typeof err !== 'object') return undefined;
  const e = err as { response?: { status?: number }; status?: number; error?: { code?: number | string } };
  if (typeof e.response?.status === 'number') return e.response.status;
  if (typeof e.status === 'number') return e.status;
  const code = e.error?.code;
  if (typeof code === 'number') return code;
  if (typeof code === 'string' && /^\d{3}$/.test(code)) return Number(code);
  return undefined;
}

/**
 * Which state a failed load should show: 403 is "no permission", 404 is
 * "not available here" (feature off / object gone), everything else is a
 * real failure with a retry. 401 is handled globally (redirect to login).
 */
export function stateKindForError(err: unknown): 'error' | 'unavailable' | 'denied' {
  const s = errorStatus(err);
  if (s === 403) return 'denied';
  if (s === 404) return 'unavailable';
  return 'error';
}

/**
 * Technical detail for the "פרטים טכניים" disclosure: the server message and
 * status, never shown in the main line (DESIGN_SYSTEM.md §11 rule 4).
 */
export function errorDetail(err: unknown): string | null {
  if (!err) return null;
  if (typeof err === 'string') return err.trim() || null;
  if (typeof err !== 'object') return null;
  const e = err as { error?: { message?: string; error_code?: string }; message?: string; response?: { data?: { error?: { message?: string } } } };
  const msg = e.error?.message || e.response?.data?.error?.message || e.message || '';
  const status = errorStatus(err);
  const code = e.error?.error_code;
  const parts = [msg.trim(), code, status ? `HTTP ${status}` : ''].filter(Boolean);
  return parts.length ? Array.from(new Set(parts)).join(' · ') : null;
}

/**
 * Value for a number that may have failed to load: a failed or still-loading
 * number is never shown as 0 (principle 5). Returns null when the caller
 * should render the placeholder ("—" + "לא נטען" on error).
 */
export function safeMetric<T>(value: T, state: { isLoading?: boolean; isError?: boolean; hasData?: boolean }): T | null {
  if (state.isLoading) return null;
  if (state.isError && !state.hasData) return null;
  return value;
}
